import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const loginMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/auth/login", {
        email,
        password
      });
      return response.json();
    },
    onSuccess: (data) => {
      if (data.success) {
        localStorage.setItem("auth_token", data.token);
        localStorage.setItem("tenant_id", data.tenantId);
        localStorage.setItem("user", JSON.stringify(data.user));
        
        toast({
          title: "Welcome back!",
          description: "You have been logged in successfully.",
        });
        
        if (data.user.role === 'super_admin') {
          setLocation("/admin");
        } else {
          setLocation("/");
        }
      }
    },
    onError: (error: any) => {
      toast({
        title: "Login Failed",
        description: error.message || "Invalid email or password",
        variant: "destructive",
      });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: "Missing Information",
        description: "Please enter both email and password",
        variant: "destructive",
      });
      return;
    }
    loginMutation.mutate();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#0D1117' }}>
      <div 
        className="w-full max-w-md rounded-xl p-8"
        style={{ 
          backgroundColor: '#161B22', 
          border: '1px solid #30363D'
        }}
      >
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-1 mb-4">
            <span className="text-2xl font-bold text-[#06B6D4]">NAVIG</span>
            <span className="text-2xl font-bold text-[#F97316]">8</span>
            <span className="text-sm text-[#8B949E] ml-2 border-l border-[#30363D] pl-2">
              SOFTWARE SOLUTIONS
            </span>
          </div>
          <p className="text-xs text-[#F97316] mb-6">AI in Motion</p>
          
          <h1 className="text-xl font-semibold text-[#06B6D4] mb-1">
            Facial Recognition Security
          </h1>
          <h2 className="text-xl font-semibold text-[#F97316] mb-2">
            Access Control
          </h2>
          <p className="text-sm text-[#8B949E]">
            Multi-Tenant Security System
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-[#06B6D4] text-sm">
              Username or Email
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your username or email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#0D1117] border-[#30363D] text-white placeholder:text-[#484F58] focus:border-[#06B6D4] focus:ring-[#06B6D4]"
              data-testid="input-email"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password" className="text-[#06B6D4] text-sm">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#0D1117] border-[#30363D] text-white placeholder:text-[#484F58] focus:border-[#06B6D4] focus:ring-[#06B6D4]"
              data-testid="input-password"
              required
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox 
              id="remember" 
              checked={rememberMe}
              onCheckedChange={(checked) => setRememberMe(checked as boolean)}
              className="border-[#30363D] data-[state=checked]:bg-[#06B6D4] data-[state=checked]:border-[#06B6D4]"
            />
            <Label htmlFor="remember" className="text-sm text-[#8B949E] cursor-pointer">
              Remember me
            </Label>
          </div>

          <button 
            type="submit" 
            className="w-full py-3 rounded-lg font-medium text-white flex items-center justify-center gap-2 transition-all"
            style={{
              background: 'linear-gradient(90deg, #F97316 0%, #FB923C 100%)',
            }}
            disabled={loginMutation.isPending}
            data-testid="button-login"
          >
            <ArrowRight className="w-4 h-4" />
            {loginMutation.isPending ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="mt-6 text-center text-sm">
          <p className="text-[#8B949E]">
            Don't have an account?{" "}
            <Link href="/register">
              <span className="text-[#06B6D4] hover:underline cursor-pointer" data-testid="link-register">
                Register here
              </span>
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}